import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import api from '../../api/axios';
import ReportDetailsDialog from './ReportDetailsDialog';

const weekDays = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const statusStyles: Record<string, { backgroundColor: string; borderColor: string; color: string }> = {
  PLANNED: { backgroundColor: '#e8f1ff', borderColor: '#1976d2', color: '#0d47a1' },
  COMPLETED: { backgroundColor: '#e8f5e9', borderColor: '#2e7d32', color: '#1b5e20' },
  POSTPONED: { backgroundColor: '#fff3e0', borderColor: '#ed6c02', color: '#9a4a00' },
  CANCELLED: { backgroundColor: '#f5f5f5', borderColor: '#757575', color: '#555' },
};

const localDateKey = (value: string | Date) => {
  const date = value instanceof Date ? value : new Date(value);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const CalendarSection: React.FC = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedMeeting, setSelectedMeeting] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get('/sales-admin/reports')
      .then((response) => setReports(response.data))
      .catch((requestError) => setError(requestError?.response?.data?.message || 'Takvim yüklenemedi.'))
      .finally(() => setLoading(false));
  }, []);

  const meetingsByDay = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    reports
      .filter((entry) => entry.type === 'MEETING' && entry.startsAt)
      .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())
      .forEach((meeting) => {
        const key = localDateKey(meeting.startsAt);
        grouped[key] = [...(grouped[key] || []), meeting];
      });
    return grouped;
  }, [reports]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
    const mondayOffset = (firstDay.getDay() + 6) % 7;
    const calendarStart = new Date(firstDay);
    calendarStart.setDate(firstDay.getDate() - mondayOffset);
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(calendarStart);
      date.setDate(calendarStart.getDate() + index);
      return date;
    });
  }, [visibleMonth]);

  const todayKey = localDateKey(new Date());
  const linkedReport = selectedMeeting
    ? reports.find((entry) => entry.type === 'REPORT' && entry.meetingId === selectedMeeting.id)
    : null;

  const changeMonth = (offset: number) => {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Toplantı Takvimi</Typography>
          <Typography color="text.secondary">Yetki alanınızdaki toplantıları aylık takvimde görüntüleyin.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Button variant="outlined" onClick={() => setVisibleMonth(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>Bugün</Button>
          <ButtonGroup variant="outlined" aria-label="Takvim ayı seçimi">
            <Button onClick={() => changeMonth(-1)} aria-label="Önceki ay">‹</Button>
            <Button onClick={() => changeMonth(1)} aria-label="Sonraki ay">›</Button>
          </ButtonGroup>
        </Stack>
      </Stack>

      <Typography variant="h5" fontWeight={700} textTransform="capitalize">
        {visibleMonth.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}
      </Typography>

      {loading ? (
        <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 980 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', bgcolor: 'grey.50' }}>
              {weekDays.map((day) => (
                <Box key={day} sx={{ px: 1.25, py: 1, borderRight: '1px solid', borderColor: 'divider', '&:last-child': { borderRight: 0 } }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">{day}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
              {calendarDays.map((date) => {
                const dateKey = localDateKey(date);
                const dayMeetings = meetingsByDay[dateKey] || [];
                const isCurrentMonth = date.getMonth() === visibleMonth.getMonth();
                const isToday = dateKey === todayKey;
                return (
                  <Box
                    key={dateKey}
                    sx={{
                      minHeight: 150,
                      p: 0.75,
                      borderTop: '1px solid',
                      borderRight: '1px solid',
                      borderColor: 'divider',
                      bgcolor: isCurrentMonth ? 'background.paper' : 'grey.50',
                      '&:nth-of-type(7n)': { borderRight: 0 },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                      <Chip
                        label={date.getDate()}
                        size="small"
                        color={isToday ? 'primary' : 'default'}
                        variant={isToday ? 'filled' : 'outlined'}
                        sx={{ opacity: isCurrentMonth ? 1 : 0.45, height: 24 }}
                      />
                    </Box>
                    <Stack spacing={0.5}>
                      {dayMeetings.map((meeting) => {
                        const style = statusStyles[meeting.status] || statusStyles.PLANNED;
                        return (
                          <Box
                            key={meeting.id}
                            component="button"
                            type="button"
                            onClick={() => setSelectedMeeting(meeting)}
                            sx={{
                              width: '100%',
                              p: 0.75,
                              textAlign: 'left',
                              border: '1px solid',
                              borderLeftWidth: 4,
                              borderRadius: 1,
                              cursor: 'pointer',
                              font: 'inherit',
                              ...style,
                              '&:hover': { filter: 'brightness(0.97)' },
                            }}
                          >
                            <Typography variant="caption" component="div" fontWeight={800} color="inherit">
                              {new Date(meeting.startsAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                            <Typography variant="caption" component="div" color="inherit" sx={{ lineHeight: 1.25, overflowWrap: 'anywhere' }}>
                              {meeting.title}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Paper>
      )}

      {!loading && !Object.keys(meetingsByDay).length && (
        <Typography color="text.secondary" textAlign="center">Henüz toplantı bulunmuyor.</Typography>
      )}

      <ReportDetailsDialog
        report={selectedMeeting}
        linkedReport={linkedReport}
        onViewLinkedReport={(report) => setSelectedMeeting(report)}
        onClose={() => setSelectedMeeting(null)}
      />
    </Stack>
  );
};

export default CalendarSection;
